import { Loader2, Maximize2, Minimize2 } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import React, { useMemo, useState } from 'react';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog';
import { BottomBarActionButtonV2 } from '@/components/custom/erp/bottom-action-bar-v2';
import type { DataTableColumn } from '@/components/custom/erp/data-table-v2';
import { ErpInputTableV2, MenuButtonV2 } from '@/components/custom/erp';
import { VI_DATETIME_DISPLAY_OPTIONS } from '@/lib/date-display';
import { DialogV2 } from '@/components/custom/erp/dialog-v2';
import { AritoIcon } from '@/components/custom/arito/icon';
import { useCRUD, useHistory } from '@/hooks';
import { useAppRouter } from '@/routing';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// HistoryDialog
// ---------------------------------------------------------------------------

interface HistoryDialogProps {
  uuid: string;
  onClose: () => void;
}

const HISTORY_LABELS = ['ID:', 'Người tạo:', 'Người sửa:', 'Ngày tạo:', 'Ngày sửa:'] as const;

const HistoryDialog = ({ onClose, uuid }: HistoryDialogProps) => {
  const { history, isLoading } = useHistory(uuid);

  return (
    <DialogV2
      open
      title='Thông tin người tạo sửa'
      titleIcon={<AritoIcon icon={260} />}
      onClose={onClose}
      maxWidth='xl'
      showFullscreenToggle={false}
      disableEscapeKeyDown
      actions={<BottomBarActionButtonV2 onClick={onClose} icon={885} primary={false} title='Đóng' />}
    >
      <div className='min-w-[300px] space-y-3 p-4'>
        <div className='space-y-2 text-sm'>
          {isLoading ? (
            <>
              {HISTORY_LABELS.map(label => (
                <div key={label} className='flex items-center gap-2'>
                  <span className='min-w-[90px] font-semibold text-gray-600'>{label}</span>
                  <span className='h-4 flex-1 animate-pulse rounded-sm bg-gray-200' />
                </div>
              ))}
            </>
          ) : (
            <>
              <div className='flex items-center gap-2'>
                <span className='min-w-[90px] font-semibold text-gray-600'>ID:</span>
                <span className='text-gray-900'>{history?.id || 'N/A'}</span>
              </div>

              <div className='flex items-center gap-2'>
                <span className='min-w-[90px] font-semibold text-gray-600'>Người tạo:</span>
                <span className='text-gray-900'>
                  {history?.username0 || 'N/A'} - {history?.nickname0 || 'N/A'} ({history?.user_id0 || 'N/A'})
                </span>
              </div>

              <div className='flex items-center gap-2'>
                <span className='min-w-[90px] font-semibold text-gray-600'>Người sửa:</span>
                <span className='text-gray-900'>
                  {history?.username2 || 'N/A'} - {history?.nickname2 || 'N/A'} ({history?.user_id2 || 'N/A'})
                </span>
              </div>

              <div className='flex items-center gap-2'>
                <span className='min-w-[90px] font-semibold text-gray-600'>Ngày tạo:</span>
                <span className='text-gray-900'>
                  {history?.datetime0
                    ? new Date(history.datetime0).toLocaleString('vi-VN', VI_DATETIME_DISPLAY_OPTIONS)
                    : 'N/A'}
                </span>
              </div>

              <div className='flex items-center gap-2'>
                <span className='min-w-[90px] font-semibold text-gray-600'>Ngày sửa:</span>
                <span className='text-gray-900'>
                  {history?.datetime2
                    ? new Date(history.datetime2).toLocaleString('vi-VN', VI_DATETIME_DISPLAY_OPTIONS)
                    : 'N/A'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </DialogV2>
  );
};

// ---------------------------------------------------------------------------
// TableDialog
// ---------------------------------------------------------------------------

interface TableDialogProps {
  uuid: string;
  onClose: () => void;
  title: string;
  endpoint?: string;
  columns: any[];
  visibleFields?: string[];
}

const PAGE_SIZE = 50;

const DEFAULT_MIN_WIDTH = 100;

const normalizeColumns = (columns: any[]): DataTableColumn[] => {
  return columns
    .filter(column => !column.hide)
    .map(column => {
      const hasExplicitWidth =
        typeof column.width === 'number' || typeof column.minWidth === 'number' || typeof column.flex === 'number';

      return {
        field: String(column.field || ''),
        headerName: column.headerName,
        width: typeof column.width === 'number' ? column.width : undefined,
        minWidth:
          typeof column.minWidth === 'number' ? column.minWidth : !hasExplicitWidth ? DEFAULT_MIN_WIDTH : undefined,
        maxWidth: typeof column.maxWidth === 'number' ? column.maxWidth : undefined,
        flex: typeof column.flex === 'number' ? column.flex : undefined,
        type: column.type,
        align: column.align as DataTableColumn['align'],
        headerAlign: ((column.headerAlign as DataTableColumn['headerAlign']) ??
          column.align) as DataTableColumn['headerAlign'],
        valueGetter: column.valueGetter as DataTableColumn['valueGetter'],
        valueFormatter: column.valueFormatter as DataTableColumn['valueFormatter'],
        renderCell: column.renderCell as DataTableColumn['renderCell']
      };
    });
};

const TableDialog = ({ onClose, uuid, columns, title, endpoint, visibleFields }: TableDialogProps) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { data, isLoading, totalItems, currentPage, handlePageChange, refreshData } = useCRUD<any, any>({
    endpoint: `${endpoint}/${uuid}`,
    pageSize: PAGE_SIZE
  });

  const rows = useMemo(() => (isLoading ? [] : ((data as Record<string, unknown>[]) ?? [])), [data, isLoading]);
  const visibleColumns = useMemo(() => {
    const nextColumns = visibleFields?.length
      ? columns.filter(column => visibleFields.includes(String(column.field)))
      : columns;
    return normalizeColumns(nextColumns);
  }, [columns, visibleFields]);

  return (
    <Dialog
      open={true}
      onOpenChange={val => {
        if (!val) onClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay className='fixed inset-0 z-[9999] bg-black/50' />
        <div className='fixed inset-0 z-[9999] flex items-center justify-center p-4'>
          <DialogPrimitive.Content
            onEscapeKeyDown={e => e.preventDefault()}
            className={cn(
              'flex w-full flex-col overflow-hidden border border-[#cfd8e3] bg-white shadow-lg outline-none transition-all duration-200',
              isFullScreen ? 'fixed inset-0 h-full max-w-none' : 'h-[80vh] max-w-[calc(100vw-64px)] rounded-sm'
            )}
          >
            {/* Header */}
            <div className='flex h-[var(--erp-dialog-header-height)] min-h-[var(--erp-dialog-header-height)] items-center justify-between border-b border-[#d1d5db] bg-[#f2f7fc] pl-3.5 pr-4'>
              <DialogTitle className='flex items-center text-[length:var(--erp-dialog-title-size)] font-bold text-black'>
                <AritoIcon icon={260} className='mx-2' />
                <span>{title}</span>
              </DialogTitle>
              <div className='flex items-center gap-1'>
                <button
                  type='button'
                  onClick={() => setIsFullScreen(prev => !prev)}
                  className='inline-flex size-8 items-center justify-center text-black/80 hover:text-black'
                  title={isFullScreen ? 'Thu nhỏ' : 'Phóng to'}
                >
                  {isFullScreen ? <Minimize2 className='size-[16px]' /> : <Maximize2 className='size-[16px]' />}
                </button>
                <button
                  type='button'
                  onClick={onClose}
                  className='inline-flex size-8 items-center justify-center text-black/80 hover:text-black'
                  title='Đóng'
                >
                  <Lineicons aria-hidden='true' icon={XmarkStroke} className='size-[18px]' />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className='relative min-h-0 min-w-0 flex-1 overflow-hidden'>
              {isLoading && (
                <div className='absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-white/70'>
                  <Loader2 className='size-9 animate-spin text-[#023e8a]' />
                  <span className='text-sm font-medium text-gray-500'>Đang tải dữ liệu...</span>
                </div>
              )}

              <div className='h-full overflow-hidden'>
                <ErpInputTableV2
                  showIndexColumn
                  rows={rows}
                  columns={visibleColumns}
                  actions={{
                    refresh: () => refreshData()
                  }}
                  actionOrder={['refresh']}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  pageSize={PAGE_SIZE}
                  onPageChange={handlePageChange}
                  height='100%'
                  hideHeader={false}
                  hideEmptyState={false}
                />
              </div>
            </div>

            {/* Footer */}
            <div className='flex justify-end gap-2 border-t border-[#e2e8f0] bg-[#f8fafc] px-4 py-2'>
              <BottomBarActionButtonV2 onClick={onClose} icon={885} title='Đóng' />
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// InfoDropdownV2
// ---------------------------------------------------------------------------

export interface InfoTableItem {
  title: string;
  icon?: number;
  group?: number;
  columns: any[];
  endpoint: string;
  visibleFields?: string[];
}

export interface InfoDropdownV2Props {
  uuid: string;
  ma_ct?: string;
  showHistory?: boolean;
  tableItems?: InfoTableItem[];
  className?: string;
}

export const InfoDropdownV2 = ({
  uuid,
  ma_ct,
  showHistory = true,
  tableItems = [],
  className = 'flex h-8 w-8 items-center justify-center bg-transparent text-gray-950 text-2xl font-normal hover:bg-slate-200 border-none shadow-none rounded-sm'
}: InfoDropdownV2Props) => {
  const [formOpen, setFormOpen] = useState<string>('none');
  const [isNavigating, setIsNavigating] = useState(false);
  const router = useAppRouter();

  const tableConfigMap = Object.fromEntries(tableItems.map((item, idx) => [`__table_${idx}`, item]));

  const menuItems = [
    ...(showHistory
      ? [
          {
            title: 'Thông tin người tạo sửa',
            icon: <AritoIcon icon={428} />,
            onClick: () => setFormOpen('history'),
            group: 0
          }
        ]
      : []),
    ...tableItems.map((item, idx) => ({
      title: item.title,
      icon: <AritoIcon icon={item.icon ?? 0} />,
      onClick: () => setFormOpen(`__table_${idx}`),
      group: item.group ?? 1
    })),
    ...(ma_ct
      ? [
          {
            title: isNavigating ? 'Đang chuyển trang...' : 'Thông tin chứng từ',
            icon: <AritoIcon icon={0} />,
            onClick: () => {
              if (!isNavigating && typeof window !== 'undefined') {
                setIsNavigating(true);
                router.push(`/he-thong/chung-tu/thong-tin-chung-tu/?ma_ct=${ma_ct}`);
              }
            },
            group: 2
          }
        ]
      : [])
  ];

  const activeTableConfig = tableConfigMap[formOpen];

  return (
    <>
      <MenuButtonV2 className={className} icon={<>𝒾</>} items={menuItems} />

      {showHistory && formOpen === 'history' && <HistoryDialog onClose={() => setFormOpen('none')} uuid={uuid} />}

      {activeTableConfig && (
        <TableDialog
          title={activeTableConfig.title}
          uuid={uuid}
          columns={activeTableConfig.columns}
          endpoint={activeTableConfig.endpoint}
          onClose={() => setFormOpen('none')}
          visibleFields={activeTableConfig.visibleFields}
        />
      )}
    </>
  );
};

export default InfoDropdownV2;
